// src/store/slices/journalSlice.ts - Journal state management

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { firestore } from '../../firebase/config';

// Define types
interface JournalEntry {
  id?: string;
  userId: string;
  content: string;
  mood?: string;
  timestamp: number;
  reflectionGenerated: boolean;
  reflectionId?: string;
}

interface Reflection {
  id?: string;
  entryId: string;
  userId: string;
  content: string;
  depthLevel: number;
  timestamp: number;
}

interface JournalState {
  entries: JournalEntry[];
  reflections: Reflection[];
  currentEntry: JournalEntry | null;
  currentReflection: Reflection | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: JournalState = {
  entries: [],
  reflections: [],
  currentEntry: null,
  currentReflection: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchJournalEntries = createAsyncThunk(
  'journal/fetchJournalEntries',
  async (userId: string, { rejectWithValue }) => {
    try {
      const entriesRef = collection(firestore, 'journalEntries');
      const q = query(
        entriesRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const entries: JournalEntry[] = [];
      
      querySnapshot.forEach((doc) => {
        entries.push({
          id: doc.id,
          ...doc.data() as JournalEntry,
        });
      });
      
      return entries;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchJournalEntry = createAsyncThunk(
  'journal/fetchJournalEntry',
  async (entryId: string, { rejectWithValue }) => {
    try {
      const entryRef = doc(firestore, 'journalEntries', entryId);
      const entrySnap = await getDoc(entryRef);
      
      if (entrySnap.exists()) {
        return {
          id: entrySnap.id,
          ...entrySnap.data() as JournalEntry,
        };
      } else {
        return rejectWithValue('Journal entry not found');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createJournalEntry = createAsyncThunk(
  'journal/createJournalEntry',
  async (entryData: Partial<JournalEntry>, { rejectWithValue }) => {
    try {
      if (!entryData.userId || !entryData.content) {
        return rejectWithValue('User ID and content are required');
      }
      
      const entriesRef = collection(firestore, 'journalEntries');
      
      const newEntry: JournalEntry = {
        userId: entryData.userId,
        content: entryData.content,
        mood: entryData.mood,
        timestamp: entryData.timestamp || Date.now(),
        reflectionGenerated: false,
      };
      
      const docRef = await addDoc(entriesRef, newEntry);
      
      return {
        id: docRef.id,
        ...newEntry,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateJournalEntry = createAsyncThunk(
  'journal/updateJournalEntry',
  async ({ entryId, updates }: { entryId: string; updates: Partial<JournalEntry> }, { rejectWithValue }) => {
    try {
      const entryRef = doc(firestore, 'journalEntries', entryId);
      
      await updateDoc(entryRef, updates);
      
      return {
        id: entryId,
        ...updates,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteJournalEntry = createAsyncThunk(
  'journal/deleteJournalEntry',
  async (entryId: string, { rejectWithValue }) => {
    try {
      const entryRef = doc(firestore, 'journalEntries', entryId);
      
      await deleteDoc(entryRef);
      
      return entryId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchReflection = createAsyncThunk(
  'journal/fetchReflection',
  async (reflectionId: string, { rejectWithValue }) => {
    try {
      const reflectionRef = doc(firestore, 'reflections', reflectionId);
      const reflectionSnap = await getDoc(reflectionRef);
      
      if (reflectionSnap.exists()) {
        return {
          id: reflectionSnap.id,
          ...reflectionSnap.data() as Reflection,
        };
      } else {
        return rejectWithValue('Reflection not found');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const createReflection = createAsyncThunk(
  'journal/createReflection',
  async (reflectionData: Partial<Reflection>, { rejectWithValue, dispatch }) => {
    try {
      if (!reflectionData.userId || !reflectionData.entryId || !reflectionData.content) {
        return rejectWithValue('User ID, entry ID, and content are required');
      }
      
      const reflectionsRef = collection(firestore, 'reflections');
      
      const newReflection: Reflection = {
        userId: reflectionData.userId,
        entryId: reflectionData.entryId,
        content: reflectionData.content,
        depthLevel: reflectionData.depthLevel || 2,
        timestamp: Date.now(),
      };
      
      const docRef = await addDoc(reflectionsRef, newReflection);
      
      // Update journal entry to mark reflection as generated
      await dispatch(updateJournalEntry({
        entryId: reflectionData.entryId,
        updates: {
          reflectionGenerated: true,
          reflectionId: docRef.id,
        },
      }));
      
      return {
        id: docRef.id,
        ...newReflection,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Create slice
const journalSlice = createSlice({
  name: 'journal',
  initialState,
  reducers: {
    clearEntries: (state) => {
      state.entries = [];
      state.reflections = [];
      state.currentEntry = null;
      state.currentReflection = null;
      state.isLoading = false;
      state.error = null;
    },
    setCurrentEntry: (state, action) => {
      state.currentEntry = action.payload;
    },
    setCurrentReflection: (state, action) => {
      state.currentReflection = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch journal entries
    builder.addCase(fetchJournalEntries.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchJournalEntries.fulfilled, (state, action) => {
      state.isLoading = false;
      state.entries = action.payload;
    });
    builder.addCase(fetchJournalEntries.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Fetch journal entry
    builder.addCase(fetchJournalEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchJournalEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentEntry = action.payload;
    });
    builder.addCase(fetchJournalEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Create journal entry
    builder.addCase(createJournalEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createJournalEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.entries = [action.payload, ...state.entries];
      state.currentEntry = action.payload;
    });
    builder.addCase(createJournalEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Update journal entry
    builder.addCase(updateJournalEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateJournalEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Update in entries array
      state.entries = state.entries.map(entry => 
        entry.id === action.payload.id 
          ? { ...entry, ...action.payload } 
          : entry
      );
      
      // Update current entry if it's the same
      if (state.currentEntry && state.currentEntry.id === action.payload.id) {
        state.currentEntry = {
          ...state.currentEntry,
          ...action.payload,
        };
      }
    });
    builder.addCase(updateJournalEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Delete journal entry
    builder.addCase(deleteJournalEntry.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteJournalEntry.fulfilled, (state, action) => {
      state.isLoading = false;
      state.entries = state.entries.filter(entry => entry.id !== action.payload);
      
      if (state.currentEntry && state.currentEntry.id === action.payload) {
        state.currentEntry = null;
      }
    });
    builder.addCase(deleteJournalEntry.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Fetch reflection
    builder.addCase(fetchReflection.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchReflection.fulfilled, (state, action) => {
      state.isLoading = false;
      state.currentReflection = action.payload;
      
      // Add to reflections array if not already there
      if (!state.reflections.some(r => r.id === action.payload.id)) {
        state.reflections.push(action.payload);
      }
    });
    builder.addCase(fetchReflection.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
    
    // Create reflection
    builder.addCase(createReflection.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createReflection.fulfilled, (state, action) => {
      state.isLoading = false;
      state.reflections.push(action.payload);
      state.currentReflection = action.payload;
    });
    builder.addCase(createReflection.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearEntries, setCurrentEntry, setCurrentReflection } = journalSlice.actions;
export default journalSlice.reducer;
